import * as d3 from "d3";
import type { TreeData, LayoutResult, NodePosition, LinkPath } from "./types";
import { dataVizClassificationPalette } from "@atlas-int/tokens";

export const LABELS = ["HS2007", "HS2002", "HS1996", "HS1992"] as const;
export const PALETTE = dataVizClassificationPalette;
export const MARGINS = { top: 120, bottom: 50, left: 70, right: 70 };

export function computeLayout(
  data: TreeData,
  svgWidth: number,
  svgHeight: number,
  _isNarrow?: boolean,
): LayoutResult {
  const numLines = 4;
  // Weight both outer gaps to be larger than the inter-line spacing.
  // Increase outer weights when width is very narrow.
  const leftGapWeight = svgWidth < 500 ? 2 : 1.5;
  const rightGapWeight = svgWidth < 500 ? (svgWidth < 400 ? 2.5 : 2) : 1.5;
  const gapCount = leftGapWeight + rightGapWeight + (numLines - 1);
  const horizontalGap = svgWidth / gapCount;
  const firstX = horizontalGap * leftGapWeight;
  const linePositions = d3
    .range(numLines)
    .map((i) => firstX + horizontalGap * i);

  const nodesByLevel = d3.group(data.nodes, (d) => d.level);
  const level1Nodes = nodesByLevel.get(1) || [];
  const level2Nodes = nodesByLevel.get(2) || [];
  const level3Nodes = nodesByLevel.get(3) || [];

  const availableHeight = svgHeight - MARGINS.top - MARGINS.bottom;

  const numSharedPositions = Math.max(level1Nodes.length, level3Nodes.length);
  const ySpacing = availableHeight / (numSharedPositions + 1);
  const sharedYPositions = d3
    .range(numSharedPositions)
    .map((i) => MARGINS.top + (i + 1) * ySpacing);

  const level2Ys: number[] = [];
  if (level2Nodes.length) {
    const n2 = level2Nodes.length;
    const topCount = Math.min(3, n2);
    const bottomCount = Math.min(3, Math.max(0, n2 - topCount));

    const baseSpacing = availableHeight / (n2 + 1);
    const initialTopYs = d3
      .range(topCount)
      .map((i) => MARGINS.top + (i + 1) * baseSpacing);

    const firstShared =
      sharedYPositions[0] ?? MARGINS.top + availableHeight * 0.25;
    const referenceIndex = Math.min(1, topCount - 1);
    const shift = firstShared - initialTopYs[referenceIndex];
    for (let i = 0; i < topCount; i++) level2Ys[i] = initialTopYs[i] + shift;

    const midStartIndex = topCount;
    const midEndIndex = n2 - bottomCount - 1;
    const midCount = Math.max(0, midEndIndex - midStartIndex + 1);
    if (midCount > 0) {
      const topGroupEndY = level2Ys[topCount - 1];
      const regionStart = topGroupEndY + 10;
      const regionEnd =
        (sharedYPositions[0] ?? MARGINS.top + availableHeight * 0.35) - 10;
      const midSpacing = (regionEnd - regionStart) / (midCount + 1);
      for (let i = 0; i < midCount; i++) {
        const idx = midStartIndex + i;
        level2Ys[idx] = regionStart + (i + 1) * midSpacing;
      }
    }

    for (let j = 0; j < bottomCount; j++) {
      const idx = n2 - bottomCount + j;
      const sharedIdx = Math.min(1 + j, sharedYPositions.length - 1);
      const sharedY =
        sharedYPositions[sharedIdx] ??
        MARGINS.top + availableHeight * (0.5 + 0.1 * j);
      level2Ys[idx] = sharedY;
    }

    if (n2 >= 4 && sharedYPositions.length >= 2) {
      const y2 = sharedYPositions[0];
      const idx4 = 3;
      const y4 = level2Ys[idx4] ?? sharedYPositions[1];
      const dGap = (y4 - y2) / 2;
      level2Ys[1] = y2;
      level2Ys[2] = y2 + dGap;
      level2Ys[0] = y2 - dGap;
    }
  }

  const nodePositions: NodePosition[] = data.nodes.map((node) => {
    let y: number;
    if (node.level === 0) {
      y = MARGINS.top + availableHeight / 2;
    } else if (node.level === 1) {
      y = sharedYPositions[level1Nodes.indexOf(node)];
    } else if (node.level === 2) {
      y =
        level2Ys[level2Nodes.indexOf(node)] ??
        MARGINS.top + availableHeight / 2;
    } else if (node.level === 3) {
      y = sharedYPositions[level3Nodes.indexOf(node)];
    } else {
      y = MARGINS.top;
    }
    return { ...node, x: linePositions[node.level], y };
  });

  const nodeMap = new Map(nodePositions.map((n) => [n.id, n]));
  const linkPaths: LinkPath[] = data.links.reduce<LinkPath[]>((acc, link) => {
    const source = nodeMap.get(link.source);
    const target = nodeMap.get(link.target);
    if (source && target) {
      acc.push({
        ...link,
        x1: source.x,
        y1: source.y,
        x2: target.x,
        y2: target.y,
      });
    }
    return acc;
  }, []);

  return { linePositions, nodePositions, linkPaths };
}
